/* İçerik editörünün form parçaları. Her metin alanı TR ve EN olarak girilir. */

import { createContext, useContext, useState, type ReactNode } from "react";
import type { I18n } from "../../shared/types.js";
import { api, ApiError } from "../api.js";

/* Çeviri ucu ancak sunucuda ANTHROPIC_API_KEY varsa açıktır; kapalıysa
   "TR'den çevir" düğmesi hiç görünmez. */
export const TranslateCtx = createContext(false);

export function Field({
  label,
  value,
  onChange,
  placeholder,
  mono,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  mono?: boolean;
}) {
  return (
    <label className="field">
      <span className="field__label">{label}</span>
      <input
        className="input"
        type="text"
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        style={mono ? { fontFamily: "ui-monospace, Menlo, monospace" } : undefined}
      />
    </label>
  );
}

export function I18nField({
  label,
  value,
  onChange,
  long,
  rows,
}: {
  label: string;
  value: I18n;
  onChange: (v: I18n) => void;
  long?: boolean;
  rows?: number;
}) {
  const Input = long ? "textarea" : "input";
  const canTranslate = useContext(TranslateCtx);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const translate = async () => {
    const source = value.tr.trim();
    if (!source || busy) return;
    if (
      value.en.trim() &&
      !window.confirm("İngilizce kutuda metin var. Çeviri üzerine yazılsın mı?")
    ) {
      return;
    }
    setBusy(true);
    setError("");
    try {
      const r = await api.translate(source);
      onChange({ ...value, en: r.text });
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Çeviri alınamadı.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="field">
      <span className="field__label">{label}</span>
      <div className="grid2">
        {(["tr", "en"] as const).map((code) => (
          <div key={code}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 8,
                minHeight: 26,
              }}
            >
              <span
                className="field__label"
                style={{ fontSize: 11, marginBottom: 4, color: "var(--gold)" }}
              >
                {code.toUpperCase()}
              </span>
              {code === "en" && canTranslate && (
                <button
                  type="button"
                  className="btn btn--ghost"
                  onClick={() => void translate()}
                  disabled={busy || !value.tr.trim()}
                  title="Türkçe metni İngilizceye çevirip bu kutuya yazar"
                  aria-label={label + " — TR'den çevir"}
                  style={{
                    minHeight: 26,
                    padding: "0 10px",
                    fontSize: 10,
                    letterSpacing: "0.08em",
                    marginBottom: 4,
                    opacity: busy || !value.tr.trim() ? 0.45 : 1,
                  }}
                >
                  {busy ? "Çevriliyor…" : "TR'den çevir"}
                </button>
              )}
            </div>
            <Input
              className="input"
              rows={long ? (rows ?? 4) : undefined}
              value={value[code]}
              onChange={(e: { target: { value: string } }) =>
                onChange({ ...value, [code]: e.target.value })
              }
            />
            {code === "en" && error && (
              <p style={{ color: "#d8695e", fontSize: 13, margin: "6px 0 0" }}>{error}</p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export function Panel({
  title,
  children,
  onRemove,
  onUp,
  onDown,
}: {
  title: string;
  children: ReactNode;
  onRemove?: () => void;
  onUp?: () => void;
  onDown?: () => void;
}) {
  return (
    <section
      className="card"
      style={{ marginTop: 16, background: "rgba(255,255,255,.03)", padding: 18 }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <h3 className="serif" style={{ fontSize: 20, margin: 0 }}>
          {title}
        </h3>
        <div style={{ display: "flex", gap: 6 }}>
          {onUp && (
            <button type="button" className="btn btn--ghost" onClick={onUp} title="Yukarı taşı">
              ↑
            </button>
          )}
          {onDown && (
            <button type="button" className="btn btn--ghost" onClick={onDown} title="Aşağı taşı">
              ↓
            </button>
          )}
          {onRemove && (
            <button type="button" className="btn btn--danger" onClick={onRemove}>
              Sil
            </button>
          )}
        </div>
      </div>
      {children}
    </section>
  );
}

/** Listeyi yeniden sıralamak için küçük yardımcı. */
export function move<T>(list: T[], from: number, to: number): T[] {
  if (to < 0 || to >= list.length) return list;
  const next = list.slice();
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
}

export const replaceAt = <T,>(list: T[], index: number, item: T): T[] =>
  list.map((x, i) => (i === index ? item : x));

export const removeAt = <T,>(list: T[], index: number): T[] =>
  list.filter((_, i) => i !== index);
